/**
 * COMMUNITY ITEM DETAIL PAGE
 *
 * Shared detail-page component for every itemType (polish, plate, cuticle
 * oil, embellishment, decal, powder, acrylic, tool). Same page skeleton —
 * hero, name/brand, Confirm, Add-to-Collection/Wishlist, Photos from
 * Collectors — with per-type differences kept minimal via the itemType
 * prop.
 *
 * Route pattern: /brands/:brandSlug/:typePath/:itemSlug
 *   `:typePath` is a literal segment (polish, plate, cuticle-care, etc.)
 *   and each route in App.tsx passes the matching `itemType` prop here.
 *
 * Phase 3 behavior:
 *   - `polish` → same query path as the old PolishDetailPage
 *     (getPolishBySlug + contributions + confirm state + official product).
 *     Behaves identically to before.
 *   - all other types → no query yet; renders the same page shell using
 *     name/brand humanized from the URL, with a placeholder note where
 *     community data will land in Phase 4. Add-to-Collection still works,
 *     writing with the correct `itemType`.
 *
 * Phase 4 will replace the non-polish placeholder with a real
 * `getCommunityItemAggregation` query (Firestore collectionGroup + new
 * composite index).
 */

import { useEffect, useState, Fragment } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, Star, Plus, Info } from 'lucide-react';
import { polishSlug as buildSlug } from '../utils/polishSlug';
import {
  getPolishBySlug,
  getPolishContributions,
  getOfficialBrandProduct,
  findOfficialBrandItemBySlug,
  type PolishContribution,
} from '../services/polishCatalogService';
import {
  getConfirmState,
  confirmPolish,
  unconfirmPolish,
} from '../services/polishConfirmService';
import { getBrandBySlug } from '../services/brandService';
import { getCommunityItemAggregation } from '../services/communityItemService';
import { getCurrentUser } from '../firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import type { Polish } from '../types/polish';
import type { Brand, AggregatedItem } from '../types/brand';
import {
  BASE_TYPE_OPTIONS,
  FINISH_OPTIONS,
  HOLO_TYPE_OPTIONS,
  SPECIAL_FEATURE_OPTIONS,
  COLOR_FAMILY_OPTIONS,
  COLOR_SHIFT_OPTIONS,
  PARTICLE_OPTIONS,
  UTILITY_OPTIONS,
  COLOR_CHARACTER_OPTIONS,
} from '../types';
import type { ItemType } from '../types';
import { canonicalizeTags, titleCaseTag } from '../utils/tagDisplay';
import PolishPhotoGallery from '../components/polish/PolishPhotoGallery';
import CopyToCollectionModal from '../components/brands/CopyToCollectionModal';
import CreationsFeaturingThis from '../components/community/CreationsFeaturingThis';
import { itemTypeMeta } from '../utils/itemTypeRoutes';

function humanizeSlug(slug: string): string {
  return slug
    .split('-')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

interface CommunityItemDetailPageProps {
  itemType: ItemType;
}

export default function CommunityItemDetailPage({ itemType }: CommunityItemDetailPageProps) {
  const { brandSlug = '', itemSlug = '' } = useParams<{
    brandSlug: string;
    itemSlug: string;
  }>();

  const isPolish = itemType === 'polish';
  const fullSlug = `${brandSlug}__${itemSlug}`;

  // Polish uses the polishCatalog document. Non-polish types (Phase 3
  // placeholder) synthesize a minimal Polish-shaped object from the URL —
  // Phase 4 replaces this with the real community aggregation query.
  const [polish, setPolish] = useState<Polish | null>(null);
  const [contributions, setContributions] = useState<PolishContribution[]>([]);
  /** Union of user-added tags across contributing items. Populated by the
   *  same query fn that returns contributions; rendered as a subtle chip
   *  strip below the finish/baseType chips. Covers all 8 itemTypes. */
  const [communityTags, setCommunityTags] = useState<string[]>([]);
  /** True when the current viewer is the owner of this polish's brand.
   *  Drives the "Edit as brand" shortcut link near the polish name. */
  const [viewerOwnsBrand, setViewerOwnsBrand] = useState(false);
  const [official, setOfficial] = useState<{ id: string; data: Record<string, any> } | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [notFoundBrand, setNotFoundBrand] = useState<Brand | null>(null);
  const [confirm, setConfirm] = useState<{ count: number; confirmed: boolean }>({ count: 0, confirmed: false });
  const [confirmBusy, setConfirmBusy] = useState(false);
  const [copyModalOpen, setCopyModalOpen] = useState(false);
  const [copyAsWishlist, setCopyAsWishlist] = useState(false);
  const [addSuccess, setAddSuccess] = useState<'collection' | 'wishlist' | null>(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setNotFound(false);

    (async () => {
      // Polish — existing catalog query path. Preserved verbatim so this
      // refactor doesn't change any polish-specific behavior.
      if (isPolish) {
        try {
          const cat = await getPolishBySlug(fullSlug);
          if (cancelled) return;

          if (!cat) {
            setNotFound(true);
            setLoading(false);
            getBrandBySlug(brandSlug)
              .then((b) => { if (!cancelled) setNotFoundBrand(b); })
              .catch((err) => console.error('brand lookup on not-found failed:', err));
            return;
          }
          setPolish(cat);
          setLoading(false);

          getPolishContributions(fullSlug)
            .then((result) => {
              if (cancelled) return;
              setContributions(result.contributions);
              setCommunityTags(result.tagsUnion);
            })
            .catch((err) => console.error('getPolishContributions failed:', err));

          getConfirmState(fullSlug, getCurrentUser()?.uid)
            .then((s) => { if (!cancelled) setConfirm(s); })
            .catch((err) => console.error('getConfirmState failed:', err));

          if (cat.verifiedBrandId && cat.officialProductId) {
            getOfficialBrandProduct(cat.verifiedBrandId, cat.officialProductId)
              .then((o) => { if (!cancelled) setOfficial(o); })
              .catch((err) => console.error('getOfficialBrandProduct failed:', err));
          }

          // If the viewer's profile carries `ownsBrandId` matching this
          // polish's verifiedBrandId, they're the brand owner — flip on
          // the "Edit as brand" shortcut. Silent on any failure; the
          // shortcut just won't render.
          const viewer = getCurrentUser();
          if (viewer && cat.verifiedBrandId) {
            getDoc(doc(db, 'users', viewer.uid))
              .then((snap) => {
                if (cancelled) return;
                const data = snap.data();
                if (data?.ownsBrandId === cat.verifiedBrandId) {
                  setViewerOwnsBrand(true);
                }
              })
              .catch((err) =>
                console.error('brand-owner check failed:', err)
              );
          }
        } catch (err) {
          console.error('CommunityItemDetailPage (polish) load failed:', err);
          if (!cancelled) {
            setNotFound(true);
            setLoading(false);
          }
        }
        return;
      }

      // Non-polish types — try both community aggregation and the brand's
      // official item in parallel. If either has data, we render the
      // page. Brand-only items (no collectors yet, but the brand added
      // an official listing) used to show "not found"; this fallback
      // fixes that so the description + brand-authoritative data is
      // still viewable on public pages.
      try {
        const [agg, official] = await Promise.all([
          getCommunityItemAggregation(itemType, brandSlug, itemSlug).catch((err) => {
            console.error('getCommunityItemAggregation failed:', err);
            return null;
          }),
          findOfficialBrandItemBySlug(brandSlug, itemSlug, itemType).catch((err) => {
            console.error('findOfficialBrandItemBySlug failed:', err);
            return null;
          }),
        ]);
        if (cancelled) return;

        if (!agg && !official) {
          setNotFound(true);
          setLoading(false);
          getBrandBySlug(brandSlug)
            .then((b) => { if (!cancelled) setNotFoundBrand(b); })
            .catch((err) => console.error('brand lookup on not-found failed:', err));
          return;
        }

        // Prefer the community aggregation's polish shape (has real
        // collector count, casing preserved from first user item, etc.).
        // Fall back to constructing a minimal Polish shape from the
        // brand item when it's the only data source.
        let polishShape;
        if (agg) {
          polishShape = agg.polish;
        } else {
          const bd = official!.data;
          polishShape = {
            slug: `${brandSlug}__${itemSlug}`,
            brand: bd.brand ?? humanizeSlug(brandSlug),
            name: bd.name ?? humanizeSlug(itemSlug),
            brandSlug,
            nameSlug: itemSlug,
            itemType,
            collectorCount: 0,
            photoCount: Array.isArray(bd.photos) ? bd.photos.length : 0,
            colorDescription: bd.colorDescription,
          } as any;
        }

        setPolish(polishShape);
        setContributions(agg?.contributions ?? []);
        setCommunityTags(agg?.tagsUnion ?? []);
        if (official) setOfficial({ id: official.id, data: official.data });
        setLoading(false);

        // Confirm state — best-effort, non-blocking.
        getConfirmState(polishShape.slug, getCurrentUser()?.uid, itemType)
          .then((s) => { if (!cancelled) setConfirm(s); })
          .catch((err) => console.error('getConfirmState (non-polish) failed:', err));
      } catch (err) {
        console.error('CommunityItemDetailPage aggregation load failed:', err);
        if (!cancelled) {
          setNotFound(true);
          setLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [fullSlug, isPolish, brandSlug, itemSlug]);

  const toggleConfirm = async () => {
    const user = getCurrentUser();
    if (!user || confirmBusy) return;
    setConfirmBusy(true);
    const next = !confirm.confirmed;
    setConfirm((c) => ({ confirmed: next, count: Math.max(0, c.count + (next ? 1 : -1)) }));
    try {
      // Confirm service routes internally by itemType: polish → polishConfirms
      // (existing collection), non-polish → itemConfirms (new). Passing
      // itemType here is what un-restricts this action from polish-only
      // that it was in Phase 3.
      if (next) await confirmPolish(fullSlug, user.uid, itemType);
      else await unconfirmPolish(fullSlug, user.uid, itemType);
    } catch (err) {
      console.error('confirm toggle failed:', err);
      setConfirm((c) => ({ confirmed: !next, count: Math.max(0, c.count + (next ? -1 : 1)) }));
    } finally {
      setConfirmBusy(false);
    }
  };

  const typeMeta = itemTypeMeta(itemType);
  const typeLabel = typeMeta?.label ?? 'Polish';

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 py-12 text-center text-glass-secondary">
        Loading {typeLabel.toLowerCase()}…
      </div>
    );
  }

  if (notFound || !polish) {
    const itemLabel = humanizeSlug(itemSlug);
    const brandLabel = notFoundBrand?.name ?? humanizeSlug(brandSlug);
    const brandExists = !!notFoundBrand;
    return (
      <div className="max-w-6xl mx-auto px-4 py-12 text-center">
        <h1 className="text-xl font-semibold text-glass mb-2">
          {brandExists ? `We couldn't find that item at ${brandLabel}` : `We couldn't find that item`}
        </h1>
        <p className="text-glass-secondary mb-6">
          {brandExists ? (
            <>
              No community entry for <em>{itemLabel}</em> under {brandLabel} yet.
              If you own it, add it to your collection to start the shared listing.
            </>
          ) : (
            <>
              We don't have a brand at <code className="px-1 rounded bg-slate-800/60">{brandSlug}</code> either — the link may be out of date.
            </>
          )}
        </p>
        <div className="flex flex-wrap gap-2 justify-center">
          <Link
            to={brandExists ? `/brands/${brandSlug}` : '/brands'}
            className="inline-flex items-center gap-1 px-4 py-2 rounded bg-purple-600/30 text-purple-300 hover:bg-purple-600/50 transition-colors"
          >
            {brandExists ? `← Back to ${brandLabel}` : 'Browse brands'}
          </Link>
        </div>
      </div>
    );
  }

  const collectors = polish.collectorCount ?? 0;
  const photos = polish.photoCount ?? 0;
  const heroPlaceholderEmoji = typeMeta?.emoji ?? '💅';

  return (
    <div className="max-w-6xl mx-auto px-4 py-6">
      <Link
        to={`/brands/${brandSlug}`}
        className="inline-flex items-center gap-1 text-sm text-glass-muted hover:text-glass mb-4"
      >
        <ArrowLeft className="w-4 h-4" />
        {polish.brand}
      </Link>

      {/* Header card */}
      <div className="bg-slate-800/50 border border-slate-600 rounded-lg overflow-hidden mb-6">
        <div className="grid md:grid-cols-[260px_1fr] gap-4">
          {/* Hero cell — stretches vertically to match the right column so
              the box never leaves an awkward gap next to the info panel.
              min-h-[260px] preserves a square-ish minimum when the right
              column is short (few tags / no confirm block). */}
          <div className="bg-slate-900 flex items-center justify-center min-h-[260px]">
            {(() => {
              // Polish hero stays brand-only (coverPhotoUrl) per task 185's
              // policy — collector photos live in the gallery below, not
              // up top pretending to be canonical. Non-polish types have
              // no verified-brand catalog yet, so they use the community
              // cover the aggregation service populated (best available
              // photo). This lets plate/oil/etc. detail pages surface a
              // hero image immediately when any collector has uploaded
              // one, without violating the brand-only rule for polish.
              const heroUrl = isPolish
                ? polish.coverPhotoUrl
                : polish.coverPhotoUrl ?? polish.communityCoverPhotoUrl;
              return heroUrl ? (
                <img
                  src={heroUrl}
                  alt={polish.name}
                  className="w-full h-full object-cover"
                />
              ) : (
                <div className="text-7xl" aria-label="No cover photo yet">
                  {heroPlaceholderEmoji}
                </div>
              );
            })()}
          </div>

          <div className="p-5 flex flex-col gap-3">
            <div>
              <h1 className="text-2xl font-bold text-glass">{polish.name}</h1>
              <p className="text-glass-secondary">by {polish.brand}</p>
              {/* Brand-owner shortcut — only rendered when the current
                  viewer's profile has ownsBrandId matching this polish's
                  verifiedBrandId. Deep-links to the Polishes tab so
                  correcting a tag is one click away from spotting it. */}
              {viewerOwnsBrand && polish.brandSlug && (
                <Link
                  to={`/brands/${polish.brandSlug}/manage?tab=brand-tools&polish=${polish.slug}`}
                  className="inline-flex items-center gap-1 mt-1 text-xs text-purple-300 hover:text-purple-200"
                >
                  Edit as brand →
                </Link>
              )}
            </div>

            {/* Polish-specific: color chips + colorFamily row. Rendered
                only when the polish catalog carries these fields.
                Non-polish types (Phase 3 placeholder) don't have them and
                simply skip. Phase 4's aggregation will provide type-
                appropriate metadata, and we'll extract per-type renderers
                at that point. */}
            {isPolish && polish.colors && polish.colors.length > 0 && (
              <div className="flex items-center gap-1.5">
                {polish.colors.slice(0, 8).map((c) => (
                  <div
                    key={c}
                    className="w-6 h-6 rounded-full border border-white/30"
                    style={{ backgroundColor: c }}
                    title={c}
                  />
                ))}
              </div>
            )}

            {/* Labeled tag rows for polish. Each row uses the horizontal
                width by pairing a right-aligned category label with a
                flex-wrap chip strip. Only rows that have values render,
                so a polish with only a Finish set shows one row, not
                five empty ones. Categories: Color family, Base, Finish,
                Holo type, Special features. Each category keeps the
                distinct chip color it had in the previous flat layout
                so viewers who learned that visual vocabulary still
                recognize it.

                Each raw value is filtered against its category's
                canonical enum (case-insensitive) before render. This
                hides junk like "Magnetic" ending up in finish[] via a
                misdirected Tag Pusher run, or "Buildable" stuffed into
                finish[] from a legacy import. Values that survive get
                title-cased on display so the modal's "Magnetic" reads
                the same as the detail page's chip. Raw data stays in
                Firestore for migration via admin tools. */}
            {isPolish && (() => {
              // Shared helpers live in utils/tagDisplay so brand-page
              // cards can render identically.
              interface Row {
                label: string;
                values: string[];
                chipClass: string;
              }
              const rows: Row[] = [];
              const cf = canonicalizeTags(polish.colorFamily, COLOR_FAMILY_OPTIONS);
              if (cf.length > 0) {
                rows.push({
                  label: 'Color family',
                  values: cf,
                  chipClass: 'bg-slate-700/60 text-glass-secondary',
                });
              }
              const bt = canonicalizeTags(
                polish.baseType ? [polish.baseType] : [],
                BASE_TYPE_OPTIONS
              );
              if (bt.length > 0) {
                rows.push({
                  label: 'Base',
                  values: bt,
                  chipClass: 'bg-fuchsia-700/40 text-fuchsia-100',
                });
              }
              const fn = canonicalizeTags(polish.finish, FINISH_OPTIONS);
              if (fn.length > 0) {
                rows.push({
                  label: 'Finish',
                  values: fn,
                  chipClass: 'bg-purple-700/40 text-purple-100',
                });
              }
              const ht = canonicalizeTags(polish.holoType, HOLO_TYPE_OPTIONS);
              if (ht.length > 0) {
                rows.push({
                  label: 'Holo type',
                  values: ht,
                  chipClass: 'bg-indigo-700/40 text-indigo-100',
                });
              }
              const cs = canonicalizeTags(
                polish.colorShift ? [polish.colorShift] : [],
                COLOR_SHIFT_OPTIONS
              );
              if (cs.length > 0) {
                rows.push({
                  label: 'Color shift',
                  values: cs,
                  chipClass: 'bg-emerald-700/40 text-emerald-100',
                });
              }
              const pt = canonicalizeTags(polish.particles, PARTICLE_OPTIONS);
              if (pt.length > 0) {
                rows.push({
                  label: 'Particles',
                  values: pt,
                  chipClass: 'bg-rose-700/40 text-rose-100',
                });
              }
              const sf = canonicalizeTags(
                polish.specialFeatures,
                SPECIAL_FEATURE_OPTIONS
              );
              if (sf.length > 0) {
                rows.push({
                  label: 'Special',
                  values: sf,
                  chipClass: 'bg-sky-700/40 text-sky-100',
                });
              }
              const ut = canonicalizeTags(polish.utility, UTILITY_OPTIONS);
              if (ut.length > 0) {
                rows.push({
                  label: 'Utility',
                  values: ut,
                  chipClass: 'bg-amber-700/40 text-amber-100',
                });
              }
              const cc = canonicalizeTags(
                polish.colorCharacter,
                COLOR_CHARACTER_OPTIONS
              );
              if (cc.length > 0) {
                rows.push({
                  label: 'Character',
                  values: cc,
                  chipClass: 'bg-pink-700/40 text-pink-100',
                });
              }
              if (rows.length === 0) return null;
              return (
                <div className="grid grid-cols-[100px_1fr] gap-x-3 gap-y-2 mt-1">
                  {rows.map((r) => (
                    <Fragment key={r.label}>
                      <div className="text-xs text-glass-muted text-right pt-1">
                        {r.label}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {r.values.map((v) => (
                          <span
                            key={v}
                            className={`px-2 py-0.5 text-xs rounded ${r.chipClass}`}
                            title={`${r.label}: ${titleCaseTag(v)}`}
                          >
                            {titleCaseTag(v)}
                          </span>
                        ))}
                      </div>
                    </Fragment>
                  ))}
                </div>
              );
            })()}

            {/* Community tags — union of user-added tags across every
                public collector's item. Hidden on POLISH detail pages
                because polish has proper structured chip strips
                (finish/baseType/etc.) and the cyan union tends to fill
                with import junk (`12345`, `leftover`) from spreadsheet
                imports that no one can curate. Kept for non-polish
                types since they don't have structured alternatives —
                tags[] is genuinely their only tag mechanism. The
                data fetch still runs so we can reinstate curated
                displays later without a data-model regression. */}
            {!isPolish && communityTags.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {communityTags.map((t) => (
                  <span
                    key={t}
                    className="px-2 py-0.5 text-xs rounded bg-cyan-700/30 text-cyan-100"
                    title={`Tag: ${t}`}
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}

            <div className="text-sm text-glass-muted">
              {collectors} collector{collectors === 1 ? '' : 's'} •{' '}
              {photos} photo{photos === 1 ? '' : 's'}
            </div>

            {/* Community confirmation — available for every itemType now
                that Phase 6 generalized the confirm service. Polish
                writes to `polishConfirms`, non-polish writes to
                `itemConfirms`; both use the same UI here. */}
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-3">
                <span
                  className={`text-sm inline-flex items-center gap-1 ${
                    confirm.count > 0 ? 'text-emerald-300' : 'text-glass-muted'
                  }`}
                >
                  {confirm.count > 0 ? `✓ Confirmed by ${confirm.count}` : 'Unconfirmed'}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    alert(
                      'Tap Confirm to vouch that this entry is a real, correctly listed item. Once enough collectors confirm, a green ✓ badge appears next to it on brand pages and search results — helping other users tell trusted community entries apart from typos or duplicates.'
                    )
                  }
                  aria-label="What does Confirm do?"
                  title="What does Confirm do?"
                  className="text-glass-muted hover:text-glass"
                >
                  <Info className="w-4 h-4" />
                </button>
                {getCurrentUser() && (
                  <button
                    onClick={toggleConfirm}
                    disabled={confirmBusy}
                    title="Confirm this is a real item, correctly listed"
                    className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded transition-colors disabled:opacity-50 ${
                      confirm.confirmed
                        ? 'bg-emerald-600/30 text-emerald-300 hover:bg-emerald-600/50'
                        : 'bg-slate-700/60 text-glass-secondary hover:bg-slate-600/60'
                    }`}
                  >
                    {confirm.confirmed ? '✓ Confirmed' : 'Confirm'}
                  </button>
                )}
              </div>
              <p className="text-xs text-glass-muted">
                Confirm this is a real item, correctly listed.
              </p>
            </div>

            <div className="flex flex-wrap gap-2 mt-2">
              <button
                type="button"
                onClick={() => {
                  if (!getCurrentUser()) {
                    alert(`Please sign in to add this ${typeLabel.toLowerCase()} to your collection.`);
                    return;
                  }
                  setCopyAsWishlist(false);
                  setCopyModalOpen(true);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-purple-600/30 text-purple-300 rounded hover:bg-purple-600/50 transition-colors"
              >
                <Plus className="w-4 h-4" /> Add to Collection
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!getCurrentUser()) {
                    alert(`Please sign in to add this ${typeLabel.toLowerCase()} to your wishlist.`);
                    return;
                  }
                  setCopyAsWishlist(true);
                  setCopyModalOpen(true);
                }}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-amber-500/20 text-amber-300 rounded hover:bg-amber-500/40 transition-colors"
              >
                <Star className="w-4 h-4" /> Wishlist
              </button>
            </div>

            {addSuccess && (
              <div className="mt-2 text-sm text-emerald-300">
                ✓ Added to your {addSuccess === 'wishlist' ? 'wishlist' : 'collection'}!
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Verified-brand banner (polish only for now — verified brands
          have polish products today; when Phase 7 propagates itemType
          through the verified brand path, other types will surface here
          too). */}
      {official && (
        <div className="bg-emerald-600/15 border border-emerald-500/40 rounded-lg p-4 mb-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="text-sm text-emerald-300 font-medium">
                Official product from {polish.brand}
              </div>
              {official.data.description && (
                <p className="text-glass-secondary text-sm mt-1 whitespace-pre-line">
                  {official.data.description}
                </p>
              )}
              {official.data.colorDescription && (
                <p className="text-glass-secondary text-sm mt-1 italic">
                  {official.data.colorDescription}
                </p>
              )}
            </div>
            {typeof official.data.price === 'number' && (
              <div className="text-glass font-semibold text-lg">
                ${official.data.price.toFixed(2)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Photo gallery — both polish and non-polish now feed the same
          type-agnostic PolishPhotoGallery. Polish gets its data from
          getPolishContributions (indexed via polishCatalog); non-polish
          gets it from getCommunityItemAggregation (querying user items
          directly). */}
      <h2 className="text-lg font-semibold text-glass mb-3">
        Photos from collectors
      </h2>
      <PolishPhotoGallery contributions={contributions} />

      {/* Manis featuring this item — community discovery for what people
          have MADE with this. Applies to every itemType including tool /
          cuticle care (users track recommendations for those too). The
          component queries independently and renders nothing when empty,
          so it doesn't get in the way when there are no manis yet. */}
      <CreationsFeaturingThis
        itemType={itemType}
        brandSlug={brandSlug}
        nameSlug={itemSlug}
      />

      {/* Sanity check: warn if we computed the slug wrong somewhere (polish only) */}
      {isPolish && polish.slug !== fullSlug && (
        <p className="text-xs text-glass-muted mt-6">
          Catalog slug: <code>{polish.slug}</code> (expected{' '}
          <code>{buildSlug(polish.brand, polish.name)}</code>)
        </p>
      )}

      {/* Copy-to-Collection modal — shared by both action buttons via the
          `asWishlist` prop. Builds an AggregatedItem shape from what we
          have on the page. Note the modal itself still hardcodes
          itemType='polish' internally today — Phase 7 fixes that to use
          `item.itemType`, at which point non-polish additions will
          correctly persist their type. Until Phase 7 lands, adding a
          plate detail page to your collection would write it as a polish.
          Documented so it's not surprising. */}
      {copyModalOpen && polish && (
        <CopyToCollectionModal
          asWishlist={copyAsWishlist}
          brandName={polish.brand}
          brandId={official ? undefined : undefined}
          onClose={() => setCopyModalOpen(false)}
          onSuccess={() => {
            setCopyModalOpen(false);
            setAddSuccess(copyAsWishlist ? 'wishlist' : 'collection');
            setTimeout(() => setAddSuccess(null), 4000);
          }}
          item={(() => {
            const officialDefaultUrl: string | undefined =
              (official?.data?.photos ?? []).find((p: any) => p?.isDefault && p?.url)?.url ??
              (official?.data?.photos ?? []).find((p: any) => p?.url)?.url;
            const carriedPhotoUrl =
              officialDefaultUrl ||
              polish.coverPhotoUrl ||
              polish.communityCoverPhotoUrl ||
              undefined;
            return {
              id: polish.slug,
              name: polish.name,
              itemType,
              colorDescription:
                official?.data?.colorDescription ?? (polish as any).colorDescription ?? undefined,
              collectionName:
                official?.data?.collectionName ?? (polish as any).collectionName ?? undefined,
              category: (polish as any).category ?? undefined,
              photos: carriedPhotoUrl ? [{ url: carriedPhotoUrl }] : [],
              colorFamily: polish.colorFamily,
              collectorCount: polish.collectorCount ?? 0,
            } as AggregatedItem;
          })()}
        />
      )}
    </div>
  );
}
